"use client";

import { useMemo } from "react";

import { usePathname } from "@/src/hooks/usePathname";
import {
  isPortfolioImportPath,
  portfolioHoldingsPath,
  portfolioImportPath,
  portfolioRootPath,
  resolvePortfolioNamespace,
  type PortfolioRouteNamespace,
} from "@/src/utils/portfolioRouting";

export function usePortfolioRouting() {
  const pathname = usePathname();

  const namespace = useMemo(
    () => resolvePortfolioNamespace(pathname || ""),
    [pathname],
  );

  const pathPrefix = useMemo(() => {
    const segments = (pathname || "").split("/").filter(Boolean);
    if (segments.length === 0) {
      return "";
    }
    const investIndex = segments.indexOf("invest");
    if (investIndex > 0) {
      return `/${segments.slice(0, investIndex).join("/")}`;
    }
    const portfolioIndex = segments.indexOf("portfolio");
    if (portfolioIndex > 0 && segments[portfolioIndex - 1] !== "invest") {
      return `/${segments.slice(0, portfolioIndex).join("/")}`;
    }
    return "";
  }, [pathname]);

  const isImportPage = useMemo(
    () => isPortfolioImportPath(pathname || ""),
    [pathname],
  );

  const buildImportPath = useMemo(
    () =>
      (ns: PortfolioRouteNamespace = namespace) =>
        portfolioImportPath(ns, pathPrefix),
    [namespace, pathPrefix],
  );

  const buildHoldingsPath = useMemo(
    () =>
      (portfolioId: string, ns: PortfolioRouteNamespace = namespace) =>
        portfolioHoldingsPath(ns, portfolioId, pathPrefix),
    [namespace, pathPrefix],
  );

  const buildRootPath = useMemo(
    () =>
      (ns: PortfolioRouteNamespace = namespace) =>
        portfolioRootPath(ns, pathPrefix),
    [namespace, pathPrefix],
  );

  return {
    pathname,
    namespace,
    pathPrefix,
    isImportPage,
    portfolioImportPath: buildImportPath(),
    portfolioHoldingsPath: buildHoldingsPath,
    portfolioRootPath: buildRootPath(),
    buildImportPath,
    buildHoldingsPath,
    buildRootPath,
  };
}
